import { generators, PlaycastMessage, PlaycastMessageGamepadSetState } from '@playcastdotio/Messaging';
import { getStateStore } from '../../stores';
import { getIpcMessagingInterface } from '../../messaging/protocols';
import { PlaycastGamepadId, PlaycastGamepadXInput, XInput, PlaycastPlayerInputAccess } from '@playcastdotio/CoreTypes';
import { ipcSend } from '../../messaging/protocols/ipc';
import { clearActivityListener, notifyInput } from '../managers/playerActivity';
import { playerInfoState } from '../../stores/shared';

const state = getStateStore('ipc');
const ipcMessaging = getIpcMessagingInterface();

let ALL_GAMEPADS_INITIALIZED = false;
const SHOULD_DROP_GAMEPADS = false;
export const MAX_GAMEPAD_SLOTS = 4;
const PLAYER_ONE_INDEX = 0;
const PLAYER_TWO_INDEX = 1;
const PLAYER_THREE_INDEX = 2;
const PLAYER_FOUR_INDEX = 3;

// Update the combined playerInfoState with current player access and gamepad slots
const updatePlayerInfoState = () => {
  const playerState = state.playerState.currentState.players;
  const gamepadState = state.gamepadState.currentState;

  const updatedPlayers: Record<string, { access: PlaycastPlayerInputAccess; gamepadSlots: number[]; displayName: string }> = {};

  // Iterate through all players and combine their access and gamepad slot info
  Object.keys(playerState).forEach((playerId) => {
    const playerAccess = playerState[playerId].access;
    const displayName = playerState[playerId].displayName;
    const gamepadSlots = gamepadState.playerMappings[playerId]?.gamepadIndexes || [];

    updatedPlayers[playerId] = {
      access: playerAccess,
      gamepadSlots: gamepadSlots,
      displayName: displayName,
    };
  });

  playerInfoState.updateState({
    players: updatedPlayers,
  });
};

export const initializeGamepadEvents = () => {
  let anyAssignments = true;
  let cachedGamepadState = state.gamepadState.currentState;
  state.gamepadState.addListener((gamepadState) => {
    let shouldTriggerAssignmentCheck = false;
    if (gamepadState.maxAssignments === 0 && anyAssignments === true) {
      anyAssignments = false;
      shouldTriggerAssignmentCheck = true;
    }
    if (cachedGamepadState.maxAssignments !== gamepadState.maxAssignments) {
      console.log('[Gamepad] Max assignments changed:', gamepadState.maxAssignments);
      anyAssignments = anyAssignmentsLeft();
      shouldTriggerAssignmentCheck = true;
    }

    // Calculate the players that have had their assignments changed, compare to cachedGamepadState
    const changedAssignments = Object.keys(cachedGamepadState.playerMappings).filter((playerId) => {
      const newAssignments = gamepadState.playerMappings[playerId]?.gamepadIndexes || [];
      const oldAssignments = cachedGamepadState.playerMappings[playerId]?.gamepadIndexes || [];
      return oldAssignments.length !== newAssignments.length;
    });
    changedAssignments.forEach((playerId) => {
      const currentAssignments = gamepadState.playerMappings[playerId]?.gamepadIndexes || [];
      const oldAssignments = cachedGamepadState.playerMappings[playerId]?.gamepadIndexes || [];
      const removedAssignments = oldAssignments.filter((assignment) => !currentAssignments.includes(assignment));
      removedAssignments.forEach((assignment) => {
        clearActivityListener(playerId, 'gamepad', assignment);
      });
    });

    // let oldGamepads = Object.values(cachedGamepadState.playerMappings).map((mapping) => mapping?.gamepadIndex);
    // oldGamepads = oldGamepads.filter((index) => index !== -1);
    // let newGamepads = Object.values(gamepadState.playerMappings).map((mapping) => mapping?.gamepadIndex);
    // newGamepads = newGamepads.filter((index) => index !== -1);

    // const removedGamepads = oldGamepads.filter((index) => !newGamepads.includes(index));
    // const addedGamepads = newGamepads.filter((index) => !oldGamepads.includes(index));

    // if (addedGamepads.length > 0) {
    //   console.log('[Gamepad] Added:', addedGamepads);
    // }
    // if (removedGamepads.length > 0) {
    //   console.log('[Gamepad] Removed:', removedGamepads);
    // }

    // if (SHOULD_DROP_GAMEPADS) {
    //   removedGamepads.forEach((gamepadIndex) => {
    //     const message = generators.gamepad.remove(state.authState.currentState.hostAuthId, gamepadIndex);
    //     ipcMessaging.send(message);
    //   });
    // }
    // if (!ALL_GAMEPADS_INITIALIZED) {
    //   addedGamepads.forEach((gamepadIndex) => {
    //     if (gamepadIndex < MAX_GAMEPAD_SLOTS && gamepadIndex >= 0) {
    //       const message = generators.gamepad.initialize(state.authState.currentState.hostAuthId, gamepadIndex);
    //       ipcMessaging.send(message);
    //     }
    //   });
    // }
    //cachedGamepadState = structuredClone(gamepadState);
    cachedGamepadState = gamepadState;
    if (shouldTriggerAssignmentCheck) {
      handleAnyAssignmentsLeft(anyAssignments);
    }

    // Update the combined playerInfoState whenever gamepad state changes
    updatePlayerInfoState();
  });
};

export const initializeAllGamepads = () => {
  ALL_GAMEPADS_INITIALIZED = true;
  console.log('[Gamepad] Initializing gamepads to reserve local gamepads for host');
  const hostGamepadState = state.hostGamepadState.currentState;
  const ids = hostGamepadState.gamepads.filter((gamepad) => gamepad.isPhysical).map((gamepad) => gamepad.gamepadId);
  const gamepadState = state.gamepadState.currentState;
  gamepadState.playerMappings.host = { gamepadIndexes: ids };
  state.gamepadState.updateState(gamepadState);

  for (let i = 0; i < MAX_GAMEPAD_SLOTS; i++) {
    const message = generators.gamepad.initialize(state.authState.currentState.hostAuthId, i as PlaycastGamepadXInput['gamepadId']);
    ipcMessaging.send(message);
  }

  ipcMessaging.send(generators.gamepad.getStatus());
};

export const dropAllGamepads = () => {
  console.log('[Gamepad] Skipping dropping all gamepads, managed at startup.');
  for (let i = 0; i < MAX_GAMEPAD_SLOTS; i++) {
    const message = generators.gamepad.remove(state.authState.currentState.hostAuthId, i as PlaycastGamepadXInput['gamepadId']);
    ipcMessaging.send(message);
  }
};

export const removeAssignmentsForPlayer = (playerId: string) => {
  const gamepadIndexes = state.gamepadState.currentState.playerMappings;
  // find all gamepadIds that start with playerId
  const gamepadIds = Object.keys(gamepadIndexes).filter((gamepadId) => {
    return gamepadId.startsWith(playerId);
  });
  if (gamepadIds.length === 0) {
    console.log('[Gamepad] Removing assignments for player:', playerId, gamepadIds);
  }
  // remove them from the state
  gamepadIds.forEach((gamepadId) => {
    delete gamepadIndexes[gamepadId];
  });
  state.gamepadState.updateState({
    ...state.gamepadState.currentState,
    playerMappings: { ...gamepadIndexes },
  });

  // Update the combined playerInfoState when assignments are removed
  updatePlayerInfoState();
};

const isAssignmentShared = (assignment: number) => {
  if (
    state.gamepadState.currentState.shareGamepadOne ||
    state.gamepadState.currentState.shareGamepadTwo ||
    state.gamepadState.currentState.shareGamepadThree ||
    state.gamepadState.currentState.shareGamepadFour
  ) {
    return true;
  }
  if (assignment === PLAYER_ONE_INDEX && state.gamepadState.currentState.shareGamepadOne) {
    return true;
  }
  if (assignment === PLAYER_TWO_INDEX && state.gamepadState.currentState.shareGamepadTwo) {
    return true;
  }
  if (assignment === PLAYER_THREE_INDEX && state.gamepadState.currentState.shareGamepadThree) {
    return true;
  }
  if (assignment === PLAYER_FOUR_INDEX && state.gamepadState.currentState.shareGamepadFour) {
    return true;
  }
  return false;
};

export const handleAnyAssignmentsLeft = (anyLeft: boolean) => {
  // disabled for now
  return;
  console.log('[Gamepad] Any assignments left:', anyLeft);
  if (anyLeft) {
    // if there are assignments left, we want to enable gamepad access for all players
    const playerState = structuredClone(state.playerState.currentState.players);
    let changed = false;
    Object.keys(playerState).forEach((playerId) => {
      if (!playerState[playerId].access.gamepad) {
        playerState[playerId].access.gamepad = true;
        changed = true;
      }
    });
    if (changed) {
      state.playerState.updateState({
        ...state.playerState.currentState,
        players: playerState,
      });
    }
  } else {
    // if there are no assignments left, we want to disable gamepad access for any players without assignments
    const rawPlayersWithAssignments = Object.keys(state.gamepadState.currentState.playerMappings).map((playerId) => {
      const assignments = state.gamepadState.currentState.playerMappings[playerId]?.gamepadIndexes;
      if (!assignments) {
        return '';
      }
      return assignments.length > 0 ? playerId : '';
    });
    const playersWithAssignments = rawPlayersWithAssignments.filter((playerId) => playerId !== '');
    const playerState = structuredClone(state.playerState.currentState.players);
    let changed = false;
    Object.keys(playerState).forEach((playerId) => {
      console.log('[Gamepad] Checking player:', playerId, playersWithAssignments);
      if (!playersWithAssignments.includes(playerId)) {
        playerState[playerId].access.gamepad = false;
        changed = true;
      }
    });
    if (changed) {
      state.playerState.updateState({
        ...state.playerState.currentState,
        players: playerState,
      });
    }
  }
};

export const setAssignmentsForPlayer = (playerId: string, assignments: number[]) => {
  // if any assignments are outside of the range, remove them
  const validAssignments = assignments.filter((assignment) => assignment < MAX_GAMEPAD_SLOTS && assignment >= 0);
  // if assignments is empty, remove all assignments from the player
  if (validAssignments.length === 0) {
    removeAssignmentsForPlayer(playerId);
    return;
  }
  // find all gamepadIds that start with playerId, if they exist.
  const gamepadIndexes = state.gamepadState.currentState.playerMappings;
  const gamepadPlayerIds = Object.keys(gamepadIndexes).filter((gamepadPlayerId) => {
    return gamepadPlayerId.startsWith(playerId);
  });
  // remove them from the state
  gamepadPlayerIds.forEach((gamepadId) => {
    delete gamepadIndexes[gamepadId];
  });

  // add the new assignments to the state, checking whether it can be shared
  validAssignments.forEach((assignment) => {
    const gamepadId = `${playerId}`;
    if (!isAssignmentShared(assignment)) {
      Object.keys(gamepadIndexes).forEach((playerGamepadId) => {
        const otherPlayerAssignments = gamepadIndexes[playerGamepadId]?.gamepadIndexes;
        if (otherPlayerAssignments) {
          // update their array to remove the assignment
          const index = otherPlayerAssignments.indexOf(assignment as PlaycastGamepadId);
          if (index !== -1) {
            otherPlayerAssignments.splice(index, 1);
            gamepadIndexes[playerGamepadId] = {
              gamepadIndexes: otherPlayerAssignments,
            };
          }
        }
      });
    }
    let thisPlayerAssignments = gamepadIndexes[gamepadId]?.gamepadIndexes;
    if (!thisPlayerAssignments) {
      thisPlayerAssignments = [];
    }
    thisPlayerAssignments.push(assignment as PlaycastGamepadId);
    gamepadIndexes[gamepadId] = {
      gamepadIndexes: thisPlayerAssignments,
    };
  });

  state.gamepadState.updateState({
    ...state.gamepadState.currentState,
    playerMappings: { ...gamepadIndexes },
  });
  const anyLeft = anyAssignmentsLeft();
  handleAnyAssignmentsLeft(anyLeft);

  // Update the combined playerInfoState when assignments are set
  updatePlayerInfoState();
};

export const anyAssignmentsLeft = (): boolean => {
  let anyAssignmentsLeft = false;
  const maxAssignments = state.gamepadState.currentState.maxAssignments;
  const uniqueAssignments = [
    ...new Set(
      Object.values(state.gamepadState.currentState.playerMappings)
        .map((mapping) => mapping?.gamepadIndexes)
        .flat(),
    ),
  ];
  if (uniqueAssignments.length < maxAssignments) {
    anyAssignmentsLeft = true;
  }
  return anyAssignmentsLeft;
};

const addNewGamepadAssignment = (remoteId: string): number => {
  if (!state.gamepadState.currentState.autoAssign) {
    return -1;
  }
  let gamepadIndexes = state.gamepadState.currentState.playerMappings[remoteId]?.gamepadIndexes;
  console.log(JSON.stringify(state.gamepadState.currentState.playerMappings, null, 2));
  if (gamepadIndexes === undefined || gamepadIndexes === null) {
    gamepadIndexes = [];
  }
  const allAssignments = Object.values(state.gamepadState.currentState.playerMappings)
    .map((mapping) => mapping?.gamepadIndexes)
    .flat();
  const deduplicatedAssignments = [...new Set(allAssignments)];
  if (deduplicatedAssignments.length >= state.gamepadState.currentState.maxAssignments) {
    return -1;
  }
  // const availableAssignmentCount = state.gamepadState.currentState.maxAssignments - deduplicatedAssignments.length;

  for (let i = 0; i < MAX_GAMEPAD_SLOTS; i++) {
    if (
      !Object.values(state.gamepadState.currentState.playerMappings).some((mapping) =>
        mapping?.gamepadIndexes.includes(i as PlaycastGamepadId),
      )
    ) {
      gamepadIndexes.push(i as PlaycastGamepadId);
      state.gamepadState.updateState({
        ...state.gamepadState.currentState,
        playerMappings: {
          ...state.gamepadState.currentState.playerMappings,
          [remoteId]: {
            gamepadIndexes: gamepadIndexes,
          },
        },
      });
      console.log('[Gamepad] Auto assigned gamepad index:', i, 'to', remoteId);
      const anyLeft = anyAssignmentsLeft();
      handleAnyAssignmentsLeft(anyLeft);

      // Update the combined playerInfoState when auto-assignment happens
      updatePlayerInfoState();
      return i;
    }
  }
  return -1;
};

const ALLOW_LIST = ['debenh.thoma@gmail.com', '@playcast.io'];

const filterXinputButtons = (remoteId: string, xInput: XInput): XInput => {
  const playerEmail = state.playerState.currentState.players[remoteId]?.email;
  const allowed = ALLOW_LIST.some((allowedEmail) => {
    return playerEmail?.endsWith(allowedEmail);
  });
  if (allowed || !state.hostRuleState.currentState.enableGamepadFiltering) {
    return xInput;
  }
  // we should filter start (0x0010) and options (0x0020) buttons
  const BUTTONS_TO_FILTER = [0x0010, 0x0020];
  const filteredButtons = xInput.wButtons & ~BUTTONS_TO_FILTER.reduce((acc, button) => acc | button, 0);
  return {
    ...xInput,
    wButtons: filteredButtons,
  };
};

export const handleIncomingGamepadMessage = (remoteId: string, playerMessage: PlaycastMessage<PlaycastMessageGamepadSetState>) => {
  const playerAssignments = Object.keys(state.gamepadState.currentState.playerMappings).filter((playerGamepadId) => {
    return playerGamepadId.startsWith(remoteId);
  });
  // console.log('[Gamepad] Player assignments:', playerAssignments);
  // console.log('[Gamepad] Player gamepad count:', playerMessage.body.message.gamepads.length);
  // console.log('[Gamepad] Player gamepad messages:', playerMessage.body.message.gamepads);

  const deviceIndexMap: { [deviceId: string]: number | undefined } = {};
  playerMessage.body.message.gamepads.forEach((device, index) => {
    const assignmentId = `${remoteId}`;
    let gamepadIndexes = state.gamepadState.currentState.playerMappings[assignmentId]?.gamepadIndexes;
    if (!gamepadIndexes) {
      gamepadIndexes = [];
    }
    let gamepadAssignment: number;
    if (gamepadIndexes.length > index) {
      gamepadAssignment = gamepadIndexes[index];
    } else {
      gamepadAssignment = -1;
    }
    if (gamepadAssignment !== -1) {
      deviceIndexMap[device.deviceId] = gamepadAssignment;
    } else {
      console.log('[Gamepad] No gamepad index found for device:', device.deviceId);
      const newGamepadIndex = addNewGamepadAssignment(remoteId);
      if (newGamepadIndex === -1) {
        deviceIndexMap[device.deviceId] = undefined;
        return console.error('[Gamepad] No gamepad slots available');
      }
      deviceIndexMap[device.deviceId] = newGamepadIndex;
    }
  });

  // console.log('[Gamepad] Device index map:', deviceIndexMap);

  const xinputMessages: (PlaycastGamepadXInput | null)[] = playerMessage.body.message.gamepads.map((gamepad, index) => {
    if (deviceIndexMap[gamepad.deviceId] !== undefined && deviceIndexMap[gamepad.deviceId] !== null) {
      const maybeGamepadId = deviceIndexMap[gamepad.deviceId];
      // console.log('[Gamepad] Gamepad index for device:', gamepad.deviceId, maybeGamepadId);

      notifyInput(remoteId, 'gamepad', maybeGamepadId as PlaycastGamepadXInput['gamepadId']);

      return {
        gamepadId: maybeGamepadId as PlaycastGamepadXInput['gamepadId'],
        playerCoordinates: playerMessage.body.message.playerCoordinates,
        xInput: filterXinputButtons(remoteId, gamepad.xInput),
      };
    } else {
      // console.log('[Gamepad] No gamepad index found for device:', gamepad.deviceId);
      return null;
    }
  });
  // flatten the xinputMessages
  // const flatXinputMessages = xinputMessages.flat();
  const playjectorMessages = xinputMessages.map((xinput) => {
    if (!xinput) {
      return null;
    }
    return generators.gamepad.setXInput(playerMessage.header.user.id, xinput, playerMessage.header.user.auth);
  });
  // console.log('[Gamepad] Sending playjector messages count:', playjectorMessages.length);
  playjectorMessages.forEach((message) => {
    if (message) ipcSend(message);
  });
};
